import { ReportTypeEnum } from '@revelio/data-access';

import type { GlobalAssistantReportCapabilityModel } from './types';

export const REPORT_CAPABILITY_MODELS: Readonly<
  Record<
    | ReportTypeEnum.AiAdoptionExposure
    | ReportTypeEnum.Attrition
    | ReportTypeEnum.Compensation
    | ReportTypeEnum.CompensationBenchmarking
    | ReportTypeEnum.GenderDiversity
    | ReportTypeEnum.HcDisclosure
    | ReportTypeEnum.JobComparison
    | ReportTypeEnum.Prestige
    | ReportTypeEnum.SalesCompensation
    | ReportTypeEnum.Skills
    | ReportTypeEnum.MarketTightness
    | ReportTypeEnum.OrgStructure
    | ReportTypeEnum.Sentiment,
    GlobalAssistantReportCapabilityModel
  >
> = {
  [ReportTypeEnum.JobComparison]: {
    outcomes: [
      'Compare two job opportunities end-to-end for candidate or recruiter decisions',
      'Evaluate economic value (compensation, retirement, benefits) for a role switch',
      'Assess experience quality via satisfaction and sentiment determinants',
      'Judge work content fit through activities and skills requirements',
      'Compare career progression, mobility, and forward-looking role and company outlook',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'Headline deltas and concise recommendations emphasizing tradeoffs, not a single winner-only framing',
      },
      {
        name: 'Compensation and benefits',
        intent:
          'Total compensation distributions, retirement package quality (US where applicable), and benefits ratings by category',
      },
      {
        name: 'Determinants of job satisfaction',
        intent:
          'Topic-level employee sentiment vs competitor set with strengths and concern areas per employer',
      },
      {
        name: 'Role content and requirements',
        intent:
          'Core activities profile and skills requirements (top, unique, and shared skills from postings)',
      },
      {
        name: 'Career growth and professional development',
        intent:
          'Promotion likelihood and compensation uplift; internal vs external mobility and top external destinations',
      },
      {
        name: 'Role outlook',
        intent:
          'Role-specific headcount growth trend and trajectory commentary',
      },
      {
        name: 'Company outlook',
        intent:
          'Company headcount growth trend and business outlook sentiment (level and YoY change)',
      },
    ],
    bestForExamples: [
      'Compare two jobs, offers, or roles across companies, metros, or seniority for a candidate decision',
      'Compare a designer role in Boston at one firm vs a product manager role in Denver at another, same seniority band',
      'Should I stay as an analyst in Chicago or take a consultant role in Dallas— which job is better overall?',
      'Evaluate moving from one bank in NYC to another bank in London for the same VP title—pay, benefits, and career outlook',
      'Evaluate a role switch economic tradeoff and benefits impact',
      'Assess fit via work content, skills expectations, and advancement prospects',
    ],
    notFor: [
      'Single-company sentiment-only deep dive without two-role comparison intent',
      'Pure labor market tightness or geography hiring difficulty without explicit two-job comparison',
      'Prestige-only reputation or education-background analysis',
    ],
    selectionCues: [
      'Paired current vs target role, company, geography, and seniority inputs',
      'Questions about which offer is better holistically, not just salary in one market',
      'Shorthand roles (PM, DE) and shared seniority on both sides still indicate Job Comparison',
    ],
  },
  [ReportTypeEnum.Prestige]: {
    outcomes: [
      'Quantify workforce prestige for a company and what drives it',
      'Benchmark prestige vs competitors and industry by role',
      'Show geographic concentration and trend direction (inflow vs outflow)',
      'Assess return on prestige (promotion, tenure) vs cost (salary premium) by role',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'Overall prestige standing vs universe and competitors with trajectory and recommendations',
      },
      {
        name: 'Educational background',
        intent:
          'University pipeline and aggregated school-quality signals as one prestige input',
      },
      {
        name: 'Corporate background',
        intent:
          'Prior-company inflow pipeline, inflow-company prestige context, and universe percentile with methodology framing',
      },
      {
        name: 'Competitor benchmarks',
        intent:
          'Company prestige rank within competitor set and role-level prestige vs industry benchmarks',
      },
      {
        name: 'Geographic differences',
        intent:
          'Prestige by country or region with map-based concentration and gaps',
      },
      {
        name: 'Trends',
        intent:
          'Workforce, inflow, and outflow prestige over time with recent movement detail tables',
      },
      {
        name: 'Return on prestige',
        intent:
          'Promotion likelihood and tenure effects associated with higher prestige, by role category',
      },
      {
        name: 'Cost of prestige',
        intent:
          'Salary premium or discount associated with higher prestige by role for ROI tradeoffs',
      },
      {
        name: 'Conclusion',
        intent:
          'Reconcile standing, competitor position, trends, inflow/outflow dynamics, and ROI/cost into guidance',
      },
    ],
    bestForExamples: [
      'Company prestige score or employer reputation ranking for a named company',
      'Prestige analysis for a biotech firm—education pipeline, competitor benchmarks, and trends',
      'How prestigious is a semiconductor company’s workforce by role and geography?',
      'How prestigious is company X’s workforce vs peers or industry',
      'Where prestige is concentrated by role or geography and whether it is improving',
      'Whether prestigious hires pay off in outcomes vs compensation cost',
    ],
    notFor: [
      'Pure sentiment, culture, or Glassdoor-style review questions without prestige framing',
      'Job-vs-job candidate comparison across two offers',
      'Market tightness or hiring difficulty by geography-role without a prestige objective',
    ],
    selectionCues: [
      'Single focus company with prestige, reputation, or education/corporate pipeline emphasis',
      'Competitor-relative and role-relative prestige framing',
    ],
  },
  [ReportTypeEnum.MarketTightness]: {
    outcomes: [
      'Compare labor markets for hiring strategy across target and comparison geographies',
      'Assess supply, demand, and demand-to-supply ratio for talent competition',
      'Evaluate salary levels, growth, and wage pressure in context of location decisions',
      'Understand time-to-fill, skills mix, demographics, talent pipeline, and competitive landscape',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'AI summary of market conditions for the selected occupation and geographies',
      },
      {
        name: 'Labor supply and demand',
        intent:
          'Headcount-based supply vs active postings demand bars for target geography (and occupation when set)',
      },
      {
        name: 'Demand-to-supply ratio',
        intent:
          'Ratio trend, percentile rank, gauge, and narrative on hiring environment tightness',
      },
      {
        name: 'Salary',
        intent:
          'Salary levels by geography, YoY salary growth, and wage-pressure percentile interpretation',
      },
      {
        name: 'Time to fill',
        intent:
          'Job posting time-to-fill metrics and rank indicating hiring agility vs difficulty',
      },
      {
        name: 'Skills',
        intent:
          'Skill composition of the workforce beyond job titles for hiring needs',
      },
      {
        name: 'Demographics',
        intent:
          'Workforce demographic mix differences across locations for diversity and perspective',
      },
      {
        name: 'Talent pipeline',
        intent:
          'Educational backgrounds and school pipelines indicating early-career talent sources',
      },
      {
        name: 'Competitive landscape',
        intent:
          'Employer concentration and competition dynamics affecting hiring cost and barriers',
      },
    ],
    bestForExamples: [
      'Labor market conditions or hiring difficulty in one metro vs another',
      'Compare hiring difficulty and salaries for electricians in two different metros',
      'Market tightness for senior engineers with specific skills in two global cities',
      'Where to hire: compare geographies for role, skill, and seniority filters',
    ],
    notFor: [
      'Job-to-job comparison for one candidate across two offers',
      'Employee sentiment, culture, or review-focused questions',
      'Company prestige or education-pipeline reputation analysis',
      'A live on-screen tightness view — the Market Conditions page covers that for 1 to 6 geographies/roles, comparison included. The split is deliverable shape (live page vs multi-section written report), NOT single-market vs comparison; offer the page alongside this report rather than routing comparisons here by default',
    ],
    selectionCues: [
      'Derived from report-company-market-conditions CustomDash template sections',
      'Target geography plus optional comparison geographies, occupation, skills, seniority',
    ],
  },
  [ReportTypeEnum.Sentiment]: {
    outcomes: [
      'Benchmark company employee sentiment vs an auto-selected peer group',
      'Break down satisfaction across workplace, growth/compensation, and leadership dimensions',
      'Compare sentiment by role and metro area for retention and engagement risks',
      'Extract topics and themes from review text via NLP',
    ],
    sections: [
      {
        name: 'Industry overview',
        intent:
          'Peer group composition from competitors model weighted by headcount',
      },
      {
        name: 'Overall sentiment',
        intent:
          'Company rank, trend, and delta vs peer average on overall employee ratings',
      },
      {
        name: 'Workplace experience',
        intent:
          'Work-life balance, culture and values, and diversity and inclusion vs peers over time',
      },
      {
        name: 'Growth and compensation',
        intent:
          'Compensation and benefits, career growth, and business outlook vs peers',
      },
      {
        name: 'Leadership',
        intent: 'Leadership and mission/vision sentiment vs peers',
      },
      {
        name: 'Breakdown by roles',
        intent:
          'Overall sentiment by top roles (minimum review volume thresholds)',
      },
      {
        name: 'Breakdown by location',
        intent: 'Overall sentiment by top metro areas vs peer companies',
      },
      {
        name: 'Review summaries and topics',
        intent:
          'Review text summaries and NLP topic/theme sentiment standardized vs talent-flow comparison set',
      },
    ],
    bestForExamples: [
      'Compare employee sentiment at two peer companies in the same industry',
      'Why do employees quit a fintech firm—reviews and culture compared to rivals',
      'Sentiment breakdown for two logistics carriers and their peer set',
      'Why do people leave or stay; culture and review comparisons vs competitors',
      'Work-life balance or leadership sentiment relative to peers',
    ],
    notFor: [
      'Prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
      'Labor market tightness, postings, or demand-supply hiring difficulty',
      'Two-offer job comparison for a single candidate',
    ],
    selectionCues: [
      'Derived from report-company-sentiment CustomDash template sections',
      'Focus company with optional competitor overrides',
    ],
  },
  [ReportTypeEnum.AiAdoptionExposure]: {
    outcomes: [
      "Benchmark a company's AI exposure and AI adoption against an auto-selected peer group",
      'Identify roles with high AI exposure but low adoption for productivity upside and automation risk',
      'Compare geographic variation in AI exposure and adoption for talent and innovation strategy',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'Overall AI exposure and adoption vs peers with role and geography highlights and recommendations',
      },
      {
        name: 'Industry overview',
        intent:
          'Peer group composition from competitors model weighted by headcount among closest companies',
      },
      {
        name: 'AI exposure compared to closest competitors',
        intent:
          'Company-level AI exposure score vs competitor set and industry average from activity taxonomy',
      },
      {
        name: 'AI adoption compared to closest competitors',
        intent:
          'Share of positions mentioning AI-related keywords vs competitors and industry average',
      },
      {
        name: 'AI exposure by role',
        intent:
          'Most and least AI-exposed roles among largest roles by headcount',
      },
      {
        name: 'AI adoption by role',
        intent:
          'Highest and lowest AI adoption roles among largest roles by headcount',
      },
      {
        name: 'AI possibility matrix by role',
        intent:
          'Bubble matrix of exposure vs adoption by role with quadrant interpretation',
      },
      {
        name: 'AI exposure by geography',
        intent:
          'Average AI exposure of employees by country on a choropleth map',
      },
      {
        name: 'AI adoption by geography',
        intent:
          'Average AI adoption of employees by country on a choropleth map',
      },
    ],
    bestForExamples: [
      "How exposed is a retail company's workforce to AI and how widely are AI tools adopted?",
      'Which roles at a company have high AI exposure but low adoption?',
      'AI automation risk and AI readiness for a named company vs peers',
      'Where is AI adoption highest geographically for a global employer?',
    ],
    notFor: [
      'Pure employee sentiment, culture, or review-focused questions without AI exposure or adoption framing',
      'Company prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
      'Labor market tightness, postings, or demand-supply hiring difficulty',
      'Two-offer job comparison for a single candidate',
    ],
    selectionCues: [
      'Single focus company with AI exposure, AI adoption, automation risk, or AI readiness framing',
      'Peer comparisons are auto-selected; user supplies company only',
    ],
  },
  [ReportTypeEnum.Compensation]: {
    outcomes: [
      "Benchmark a company's salary and total compensation vs an auto-selected peer group",
      'Identify geographic and role-level pay gaps vs peers for retention and site-selection decisions',
      'Assess salary satisfaction and competitors as talent sources based on relative pay',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'Overall compensation competitiveness vs peers with geography, role, satisfaction, and talent-source highlights and recommendations',
      },
      {
        name: 'Industry overview',
        intent:
          'Peer group composition from competitors model weighted by headcount among 50 closest companies',
      },
      {
        name: 'Salary distribution compared to peer group',
        intent:
          'Salary and total compensation percentile tables for company vs peer aggregate',
      },
      {
        name: 'Salary distribution spread compared to peer group',
        intent:
          '10th through 90th percentile salary distribution bars for company vs peers',
      },
      {
        name: 'Salary distribution compared to competitors',
        intent:
          'Salary percentile distributions for company vs 15 closest named competitors',
      },
      {
        name: 'Analysis by geography',
        intent:
          'Average salary of current employees by country on a choropleth map',
      },
      {
        name: 'Analysis by geography relative to peers',
        intent:
          'Percent difference in average salary by country vs peers controlling for role',
      },
      {
        name: 'Employee salaries in top 20 locations',
        intent:
          'Salary percentile distributions in top metro areas by employee headcount',
      },
      {
        name: 'Salary in top 20 metro areas compared to peers',
        intent:
          'Percent salary difference vs peers in top metro areas controlling for role',
      },
      {
        name: 'Salary in top 20 roles compared to peers',
        intent:
          'Percent salary difference vs peers for most common roles controlling for location',
      },
      {
        name: 'Salary satisfaction',
        intent:
          'Composite salary satisfaction score from peer pay, labor market, and compensation sentiment',
      },
      {
        name: 'Potential sources of talent',
        intent:
          'Top competitors paying less than the company for similar roles and geographies',
      },
    ],
    bestForExamples: [
      "How does a retail company's compensation strategy compare to peers?",
      'Salary and total compensation benchmarking for a named company vs competitors',
      'Where are employees underpaid by geography or role relative to peers?',
      'Salary satisfaction and which competitors are talent sources based on lower pay',
    ],
    notFor: [
      'Two-offer job comparison for a single candidate',
      'Labor market tightness, postings, or demand-supply hiring difficulty',
      'Pure employee sentiment, culture, or review-focused questions without compensation benchmarking framing',
      'Company prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
    ],
    selectionCues: [
      'Single focus company with compensation strategy, salary benchmarking, pay by geography or role, or talent acquisition by pay framing',
      'Peer comparisons are auto-selected; user supplies company only',
    ],
  },
  [ReportTypeEnum.Attrition]: {
    outcomes: [
      "Benchmark a company's attrition and turnover vs top talent-outflow destinations",
      'Identify high-attrition roles and metros and where former employees are going',
      'Diagnose retention drivers via sentiment and pay vs outflow competitors',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'Overall attrition vs outflow peers with role and geography hotspots and retention recommendations',
      },
      {
        name: 'Attrition rate comparison',
        intent:
          'Company attrition rate vs top 15 outflow destinations over a 12-month workforce-leaving period',
      },
      {
        name: 'Top outflow destinations',
        intent:
          'Where former employees go; top 15 destinations over five years used as the comparison set for the report',
      },
      {
        name: 'Overall sentiment comparison',
        intent:
          'Sentiment among top outflow destinations, trend over time, and normalized overall satisfaction score',
      },
      {
        name: 'Overall salary comparison',
        intent:
          'Salary percentile distribution vs top outflow companies for retention risk context',
      },
      {
        name: 'Attrition rate by role comparison',
        intent:
          'Attrition for top roles by headcount vs outflow peer group with percentage-point gaps',
      },
      {
        name: 'Sentiment and salary for highest-attrition role',
        intent:
          'Role-level sentiment and salary distribution for the role with highest relative attrition',
      },
      {
        name: 'Attrition rate by metro area',
        intent:
          'Attrition in top metro areas by headcount vs outflow peer group',
      },
      {
        name: 'Sentiment and salary for highest-attrition metro',
        intent:
          'Metro-level sentiment and salary distribution for the geography with highest relative attrition',
      },
      {
        name: 'Recommendations',
        intent:
          'Targeted retention actions for high-turnover roles and geographies including pay and mobility',
      },
    ],
    bestForExamples: [
      "How does a company's attrition compare to where its employees are going?",
      'Where are former employees leaving to and which roles or metros have the highest turnover?',
      'Retention analysis for a named company vs top outflow destinations',
      'Why are employees leaving—attrition, sentiment, and pay vs talent competitors',
    ],
    notFor: [
      'Two-offer job comparison for a single candidate',
      'Labor market tightness, postings, or demand-supply hiring difficulty',
      'Compensation benchmarking without attrition or retention framing',
      'Pure employee sentiment, culture, or review-focused questions without attrition framing',
      'Company prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
    ],
    selectionCues: [
      'Single focus company with attrition, turnover, retention, or employee-outflow framing',
      'Outflow destination comparison set is auto-selected; user supplies company only',
    ],
  },
  [ReportTypeEnum.GenderDiversity]: {
    outcomes: [
      "Benchmark a company's gender composition and share of women vs industry peers",
      'Track female share over time, peer ranking, and inflow/outflow drivers of gender mix',
      'Diagnose gender gaps in pay equity, seniority imbalance, promotion, and culture by role and geography',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'Overall gender diversity vs industry with role, seniority, and geography highlights and recommendations',
      },
      {
        name: 'Share of women over time',
        intent:
          'Trend of female workforce share vs industry peers from competitors model',
      },
      {
        name: 'Ranking among peers',
        intent:
          'Female share ranking among top competitors in the comparison set',
      },
      {
        name: 'Inflows and outflows',
        intent:
          'Share of women in headcount vs inflows and outflows indicating turnover drivers of gender mix',
      },
      {
        name: 'Women representation by role',
        intent: 'Female share by most common roles vs industry average',
      },
      {
        name: 'Women representation by seniority',
        intent:
          'Female share at junior, mid, and senior levels vs industry average',
      },
      {
        name: 'Women pay equity',
        intent:
          'Dollars earned by women for every dollar earned by men vs industry',
      },
      {
        name: 'Seniority imbalance score',
        intent:
          'Score from -1 to 1 measuring whether women are concentrated below median seniority',
      },
      {
        name: 'Probability of promotion by gender',
        intent: 'Promotion rates for women vs men across seniority transitions',
      },
      {
        name: 'Company culture',
        intent:
          'Sentiment ratings for women vs men across culture and career dimensions',
      },
      {
        name: 'Problem areas by role and seniority',
        intent:
          'Heatmap of female share gaps vs industry by role and seniority level',
      },
      {
        name: 'Problem areas by role and country',
        intent:
          'Heatmap of female share gaps vs industry by role and geography',
      },
      {
        name: 'Areas of improvement',
        intent:
          'Table of granularities with lowest female share vs industry weighted by headcount',
      },
      {
        name: 'Root cause deep dive',
        intent:
          'Inflows, outflows, salary, seniority imbalance, and promotion analysis for the most problematic granularity',
      },
      {
        name: 'Recommendations',
        intent:
          'Retention and D&I actions for underrepresented granularities including pay, promotion, and culture',
      },
    ],
    bestForExamples: [
      "How does a gaming company's gender diversity compare to industry peers?",
      'Share of women and female representation by role or seniority at a named company',
      'Gender pay equity and seniority imbalance for a company vs competitors',
      'Where are women underrepresented and what drives female attrition by role or geography',
    ],
    notFor: [
      'Pure employee sentiment, culture, or review-focused questions without gender framing',
      'Attrition or turnover analysis without a gender diversity focus',
      'Compensation benchmarking without a gender-equity focus',
      'Labor market tightness, postings, or demand-supply hiring difficulty',
      'Two-offer job comparison for a single candidate',
      'Company prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
    ],
    selectionCues: [
      'Single focus company with gender diversity, gender composition, share of women, or female representation framing',
      'Peer comparison set is auto-selected; user supplies company only',
    ],
  },
  [ReportTypeEnum.HcDisclosure]: {
    outcomes: [
      "Gather and report a company's core human capital metrics for disclosure",
      'Show headcount composition across geography, role, gender, and ethnicity',
      'Summarize contingent worker share, payroll, growth, hiring, and attrition for a single company',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'Overall headcount, salaries, contingent worker share, and demographic highlights for disclosure',
      },
      {
        name: 'Overview',
        intent:
          'Company information, total global headcount over time, and human capital overview metrics including full-time headcount, contingent worker share, growth, hiring, attrition, internal hiring share, average and median salary, total payroll, and US vs global comparison',
      },
      {
        name: 'Geographic breakdown',
        intent:
          'Headcount and share by country and metro area with metro distribution visualization',
      },
      {
        name: 'Role breakdown',
        intent:
          'Headcount by job category and role with role distribution visualization',
      },
      {
        name: 'Gender demographics',
        intent: 'Overall gender breakdown and gender share by seniority level',
      },
      {
        name: 'Ethnicity demographics',
        intent: 'Ethnicity breakdown and ethnicity share by seniority level',
      },
    ],
    bestForExamples: [
      "What is a named company's headcount and employee breakdown for human capital disclosure?",
      'Human capital metrics, contingent worker share, and total payroll for a company',
      'Workforce composition by geography, role, gender, or ethnicity for disclosure reporting',
      'How many employees does a company have and how are they distributed by location and role',
    ],
    notFor: [
      'Benchmarking gender composition or pay equity vs peers',
      'Compensation or salary benchmarking vs peers',
      'Attrition or turnover analysis',
      'Pure employee sentiment, culture, or review-focused questions',
      'Labor market tightness, postings, or demand-supply hiring difficulty',
      'Company prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
      'Two-offer job comparison for a single candidate',
    ],
    selectionCues: [
      'Single focus company with human capital disclosure, headcount reporting, or employee composition framing',
      'Company-only self-disclosure with no comparison set; user supplies company only',
    ],
  },
  [ReportTypeEnum.Skills]: {
    outcomes: [
      "Benchmark a company's workforce skills and competencies vs peers",
      'Identify growing or declining skills and supply-demand gaps from profiles and postings',
      'Assess skill salary premiums and build-vs-buy for L&D, internal mobility, and strategic hiring',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'Skill prevalence, sourcing, salary premiums, and build-vs-buy highlights with recommendations',
      },
      {
        name: 'Industry overview',
        intent:
          'Peer group composition from competitors model with 50 closest companies weighted by headcount',
      },
      {
        name: 'Skill profile',
        intent:
          'Most prevalent skills and skill shares vs closest competitors highlighting strengths and deficiencies',
      },
      {
        name: 'Growing skills',
        intent:
          'Five-year workforce skill growth and decline from employee profiles and job postings',
      },
      {
        name: 'Skill supply and demand comparison',
        intent:
          'Skill surpluses and deficits comparing employee profile listings to job posting requirements',
      },
      {
        name: 'Salary premium of skills',
        intent:
          'Over- and undervalued skills vs peers from posting salary data',
      },
      {
        name: 'Building vs. buying skills',
        intent:
          'Build-vs-buy ratio and percentile rank vs competitors for internal vs external sourcing',
      },
      {
        name: 'Skill development matrix',
        intent:
          'Salary premium vs build-vs-buy quadrants for L&D and upskilling prioritization',
      },
      {
        name: 'Recommendations',
        intent:
          'Internal hiring, L&D targets, and upskilling reallocation guidance',
      },
    ],
    bestForExamples: [
      "What are a named company's workforce skills and competencies vs peers?",
      'Which skills are growing or in deficit at a company',
      'Salary premium by skill and build-vs-buy opportunities for upskilling or L&D',
      'Internal mobility and strategic hiring based on skill supply and demand gaps',
    ],
    notFor: [
      'Salary or compensation benchmarking without a skills focus',
      'Labor market tightness or hiring difficulty by geography',
      'AI exposure or adoption analysis',
      'Pure employee sentiment, culture, or review-focused questions',
      'Company prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
      'Attrition or turnover analysis',
      'Gender diversity or gender composition benchmarking',
      'Two-offer job comparison for a single candidate',
    ],
    selectionCues: [
      'Single focus company with skills, competencies, upskilling, L&D, skill gaps, or build-vs-buy framing',
      'Peer comparison set is auto-selected; user supplies company only',
    ],
  },
  [ReportTypeEnum.SalesCompensation]: {
    outcomes: [
      "Benchmark a company's sales-force compensation (base, OTE, variable) vs competitors by role and division",
      'Assess salary satisfaction, highest potential earnings, and sales pay vs other internal job categories',
      'Identify sales comp strengths and gaps for account executives, account managers, and SDRs for talent acquisition and retention',
    ],
    sections: [
      {
        name: 'Executive summary',
        intent:
          'Sales compensation strengths and gaps by role with recommendations from Revelio Labs and RepVue data',
      },
      {
        name: 'Industry overview',
        intent:
          'Competitor set composition by share of RepVue salary submissions',
      },
      {
        name: 'Sales employee overview by role',
        intent:
          'Share of salary submissions by sales role vs competitors for sample context',
      },
      {
        name: 'Sales compensation breakdown by role',
        intent:
          'Base pay and on-target earnings including variable compensation per sales role vs competitors',
      },
      {
        name: 'Account executive compensation',
        intent:
          'Account executive base pay and on-target earnings vs competitors',
      },
      {
        name: 'Account manager compensation',
        intent:
          'Account manager base pay and on-target earnings vs competitors',
      },
      {
        name: 'Sales development representative compensation',
        intent:
          'SDR, BDR, and LDR base pay and on-target earnings vs competitors',
      },
      {
        name: 'Account executive overview by division',
        intent:
          'Share of AE salary submissions by division such as Enterprise, Mid-market, and SMB vs competitors',
      },
      {
        name: 'Compensation breakdown by division',
        intent:
          'Account executive base pay and on-target earnings by division vs competitors',
      },
      {
        name: 'Salary in top 20 metro areas',
        intent:
          'Percent difference in sales employee compensation vs competitors by metro area',
      },
      {
        name: 'On-target earnings over time',
        intent:
          'Six-month rolling average on-target earnings trend vs competitors',
      },
      {
        name: 'Salary satisfaction',
        intent:
          'Salary Satisfaction Score combining relative OTE, labor supply and demand, and compensation sentiment for attrition risk',
      },
      {
        name: 'Highest potential earnings',
        intent:
          'Average highest potential earnings of sales employees vs competitors',
      },
      {
        name: 'Salary by job category',
        intent:
          'Sales salary percentile distribution vs other job categories within the company',
      },
    ],
    bestForExamples: [
      "How does a named company's sales compensation compare to competitors?",
      'Account executive, account manager, or SDR base pay and on-target earnings vs peers',
      'Sales compensation by division or metro area and salary satisfaction for the sales force',
      'Highest potential earnings and sales pay relative to other job categories at a company',
    ],
    notFor: [
      'Org-wide or non-sales compensation and salary benchmarking',
      'Labor market tightness or hiring difficulty by geography',
      'Workforce skills or competencies analysis',
      'Pure employee sentiment, culture, or review-focused questions',
      'Attrition or turnover analysis without a sales-pay focus',
      'Company prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
      'Two-offer job comparison for a single candidate',
    ],
    selectionCues: [
      'Single focus company with sales compensation, sales roles, OTE, base vs variable, or sales force pay framing',
      'Focus company with optional explicit comparison company group overrides',
    ],
  },
  [ReportTypeEnum.OrgStructure]: {
    outcomes: [
      "Visualize a named company's reporting hierarchy and leadership structure",
      'Explore who reports to whom, span of control, and management layers',
      'Map departments, divisions, and organizational layers for a single company',
    ],
    sections: [
      {
        name: 'Organizational chart',
        intent:
          'Interactive hierarchy of employees from the CEO down with expand/collapse navigation',
      },
      {
        name: 'Leadership and reporting lines',
        intent:
          'Executive and senior leadership roles with direct reporting relationships',
      },
      {
        name: 'Departments and divisions',
        intent:
          'Functional and divisional groupings within the company hierarchy',
      },
    ],
    bestForExamples: [
      'Show the org chart for a named company',
      'Who reports to the CEO or leadership structure of a company',
      "How is a company's hierarchy or reporting lines organized",
      'Organizational structure and management layers at a company',
    ],
    notFor: [
      'Headcount composition or human-capital disclosure metrics',
      'Gender composition or gender pay equity analysis',
      'Compensation or salary benchmarking',
      'Pure employee sentiment, culture, or review-focused questions',
      'Company prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
      'Two-offer job comparison for a single candidate',
    ],
    selectionCues: [
      'Single focus company with org chart, organizational structure, hierarchy, or reporting-line framing',
      'Company-only interactive org chart; user supplies company only',
    ],
  },
  [ReportTypeEnum.CompensationBenchmarking]: {
    outcomes: [
      "Benchmark a company's pay for a specific role in a specific country against industry peers",
      'Compare base salary and total compensation percentiles for a role and geography vs a peer group',
      'Identify which competitors pay more or less for the same role and country to inform pay decisions',
    ],
    sections: [
      {
        name: 'Industry overview',
        intent:
          'Peer group selected from the competitors model and headcount for the chosen role and geography',
      },
      {
        name: 'Peer group composition',
        intent:
          'The closest peer companies and their relative weighting within the benchmark set',
      },
      {
        name: 'Salary distribution compared to peer group',
        intent:
          'Base salary and total compensation for the selected role and geography vs the peer group',
      },
      {
        name: 'Salary spread and percentile distribution',
        intent:
          '10th to 90th percentile distribution of pay for the role and geography vs peers',
      },
      {
        name: 'Salary distribution compared to competitors',
        intent:
          "Company-level comparison of the company's pay against its closest competitors for the role and geography",
      },
    ],
    bestForExamples: [
      'How does a company pay a specific role in a country vs industry peers',
      'Compensation benchmarking for software engineers at a company in the US',
      "Is a company's pay for a role above or below its competitors in a country",
      'Base salary and total compensation percentiles for a role and geography vs peers',
    ],
    notFor: [
      'Org-wide compensation strategy or salary benchmarking without a specific role and country (prefer Compensation Summary)',
      'Labor market tightness, hiring difficulty, or market conditions (prefer Market Conditions)',
      'Two-offer job comparison for a single candidate (prefer Job Comparison)',
      'Sales-force compensation, on-target earnings, or commission (prefer Sales Compensation)',
      'Pure employee sentiment, culture, or review-focused questions',
      'Company prestige scores, employer reputation rankings, or education/corporate pipeline prestige',
    ],
    selectionCues: [
      'Single focus company with a specific role and a specific country plus a pay-vs-peers or pay-vs-competitors framing',
      'Requires company, occupation (role), and country; the peer group is auto-selected from the competitors model',
    ],
  },
};
